/* eslint-disable max-lines */
import { useSetAtom } from "jotai";
import React, { type FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";

import { onlyOnePlayingPlayerAtom } from "scmp-app/components/article/article-list/hooks";
import { useArticleSpeechContext } from "scmp-app/components/article/article-speech/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";

import { textToSpeechPlayerAtom } from "./atoms";
import {
  ButtonGroupContainer,
  CenterButtonGroupContainer,
  CollapsedPlayerContainer,
  CollapsedPlayerLeftContainer,
  CollapsedPlayerPlayIconContainer,
  CollapsedPlayerRightContainer,
  CollapseIconContainer,
  Container,
  ExpandedPlayerContainer,
  ExpandIconContainer,
  FlagIconContainer,
  GrayCircle,
  HeaderContainer,
  LeftContainer,
  PlaybackRateButton,
  PlaybackRateSliderContainer,
  PlayerContainer,
  PlayIconContainer,
  PopoverAndSlider,
  PopoverContainer,
  PopoverPlaybackRateText,
  PopoverTextContainer,
  RemainingTimeContainer,
  RightContainer,
  SeekBar,
  SeekBarContainer,
  Slider,
  SliderContainer,
  SliderLabel,
  SliderLabelContainer,
  StyledBackwardIcon,
  StyledBlackVoiceIcon,
  StyledChevronDownIcon,
  StyledCollapseIcon,
  StyledExpandIcon,
  StyledForwardIcon,
  StyledGrayVoiceIcon,
  StyledPauseIcon,
  StyledPlayIcon,
  StyledPopoverIcon,
  StyledRestartIcon,
  StyledTickIcon,
  StyledUKFlagIcon,
  StyledUSFlagIcon,
  SubTitleText,
  TextContainer,
  ThumbnailContainer,
  ThumbnailImage,
  Time,
  Title,
  TitleBar,
  TitleText,
  VoiceGrid,
  VoiceInfoRow,
  VoiceList,
  VoiceListContainer,
  VoiceName,
  VoiceTextContainer,
  WPMText,
} from "./styles";
import { format, getDisplayRemainingMinutesAndSeconds, getWPM } from "./utils";

const countryInfoMap = {
  "United Kingdom": {
    displayName: "United Kingdom",
    icon: <StyledUKFlagIcon />,
  },
  "United State": {
    displayName: "United States",
    icon: <StyledUSFlagIcon />,
  },
} as Record<string, { displayName: string; icon: JSX.Element }>;

export const TextToSpeechPlayer: FunctionComponent = () => {
  const {
    durationChangeEventQueue,
    setDurationChangeEventQueue,
    setTextToSpeechPlayerState: setState,
    textToSpeechPlayerState: state,
  } = useArticleSpeechContext();

  const {
    collapseStatus,
    duration,
    entityId,
    hasTriggeredThreeSeconds,
    playbackRate,
    played,
    seeking,
    selectedVoiceIndex,
    shouldShowPlaybackRateSlider,
    shouldShowVoiceList,
    src,
    status,
    thumbnail,
    title,
    voiceNameInfoList,
  } = state;

  const adjustedDuration = duration / playbackRate;

  const [playerElement, setPlayerElement] = useState<HTMLVideoElement | undefined>();
  const triggeredPercentageList = useRef([-1]);

  const setOnlyOnePlayingPlayerAtom = useSetAtom(onlyOnePlayingPlayerAtom);

  const scrollStartReference = useRef<null | number>(null);
  const lastScrollYReference = useRef(0);
  const scrollTimeoutReference = useRef<NodeJS.Timeout | null>(null);

  const setTextToSpeechPlayerAtom = useSetAtom(textToSpeechPlayerAtom);

  useEffect(() => {
    const scrollThreshold = 50;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (scrollStartReference.current === null) {
        scrollStartReference.current = currentScrollY;
        lastScrollYReference.current = currentScrollY;
        return;
      }

      const totalScrollDistance = Math.abs(currentScrollY - scrollStartReference.current);
      if (totalScrollDistance >= scrollThreshold) {
        setTextToSpeechPlayerAtom({
          isScrollingOverThreshold: true,
        });
      }

      const currentScrollDistance = Math.abs(currentScrollY - lastScrollYReference.current);
      if (currentScrollDistance < 1) {
        scrollStartReference.current = null;
        setTextToSpeechPlayerAtom({
          isScrollingOverThreshold: false,
        });
      }

      lastScrollYReference.current = currentScrollY;

      if (scrollTimeoutReference.current) {
        clearTimeout(scrollTimeoutReference.current);
      }

      scrollTimeoutReference.current = setTimeout(() => {
        scrollStartReference.current = null;
        setTextToSpeechPlayerAtom({
          isScrollingOverThreshold: false,
        });
        scrollTimeoutReference.current = null;
      }, 200);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);

      if (scrollTimeoutReference.current) {
        clearTimeout(scrollTimeoutReference.current);
      }
    };
  });

  const setPlayerReference = useCallback(
    (player: HTMLVideoElement) => {
      if (!player) return;
      setPlayerElement(player);

      setOnlyOnePlayingPlayerAtom(previousState => ({
        ...previousState,
        textToSpeechPlayer: player,
      }));
    },
    [setOnlyOnePlayingPlayerAtom],
  );

  const collapsePlayer = () => {
    setState(previousState => ({
      ...previousState,
      collapseStatus: "collapsed",
    }));
  };

  const expandPlayer = () => {
    setState(previousState => ({
      ...previousState,
      collapseStatus: "expanded-by-user",
    }));
  };

  const handleDurationChange = () => {
    if (!playerElement) return;

    const updatedDuration = playerElement.duration;
    setState(previousState => ({ ...previousState, duration: updatedDuration }));
    if (durationChangeEventQueue.length > 0) {
      const adjustedDuration = updatedDuration / playbackRate;
      durationChangeEventQueue.forEach(event => event(adjustedDuration));
      setDurationChangeEventQueue([]);
    }
  };

  const handleEnded = () => {
    setState(previousState => ({ ...previousState, status: "ended" }));

    sendGA4Tracking({
      action: "sys",
      category: "tts",
      customized_parameters: {
        action_type: "finished",
        article_id: entityId,
        audio_length: Math.ceil(adjustedDuration),
        progress_fraction: 1,
      },
      subcategory: "",
    });

    triggeredPercentageList.current = [-1];
  };

  /**
   * This event handler is required to update the status from `playing` to `paused`
   * because the `playing` property of the ReactPlayer can be updated.
   * Calling `latestTextToSpeechPlayer.current?.pause()` in `useOnlyOnePlayingPlayer` hook
   * could make the text-to-speech player pause
   * rather than triggering the `play` event listener defined in `useOnlyOnePlayingPlayer` hook
   */
  const handlePause = () => {
    setState(previousState => ({ ...previousState, status: "paused" }));
  };

  const handlePlay = () => {
    setState(previousState => ({ ...previousState, status: "playing" }));

    if (adjustedDuration === 0) {
      setDurationChangeEventQueue(previousQueue => [
        ...previousQueue,
        updatedDuration => {
          sendGA4Tracking({
            action: "sys",
            category: "tts",
            customized_parameters: {
              action_type: "play",
              article_id: entityId,
              audio_length: Math.ceil(updatedDuration),
              progress_fraction: played,
            },
            subcategory: "",
          });
        },
      ]);
      return;
    }

    sendGA4Tracking({
      action: "sys",
      category: "tts",
      customized_parameters: {
        action_type: "play",
        article_id: entityId,
        audio_length: Math.ceil(adjustedDuration),
        progress_fraction: played,
      },
      subcategory: "",
    });
  };

  const handlePlayIconClick = () => {
    if (playerElement && status === "ended") {
      playerElement.currentTime = 0;

      setState(previousState => ({
        ...previousState,
        played: 0,
        playedSeconds: 0,
        status: "playing",
      }));

      sendGA4Tracking({
        action: "click",
        category: "tts",
        customized_parameters: {
          action_type: "resume",
          article_id: entityId,
          audio_length: Math.ceil(adjustedDuration),
          progress_fraction: played,
        },
        subcategory: "",
      });

      return;
    }

    setState(previousState => {
      const updatedStatus = previousState.status === "playing" ? "paused" : "playing";

      const actionType = previousState.status === "playing" ? "pause" : "resume";

      sendGA4Tracking({
        action: "click",
        category: "tts",
        customized_parameters: {
          action_type: actionType,
          article_id: entityId,
          audio_length: Math.ceil(adjustedDuration),
          progress_fraction: played,
        },
        subcategory: "",
      });

      if (actionType === "pause") {
        /**
         * The player will trigger pause event before ended.
         * Move the pause tracking here instead of putting in the `onPause` handler
         * to avoid triggering unnecessary pause event when ended
         */
        sendGA4Tracking({
          action: "sys",
          category: "tts",
          customized_parameters: {
            action_type: "pause",
            article_id: entityId,
            audio_length: Math.ceil(adjustedDuration),
            progress_fraction: played,
          },
          subcategory: "",
        });
      }

      return {
        ...previousState,
        status: updatedStatus,
      };
    });
  };

  const handlePlaybackRateChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const inputTarget = event.target as HTMLInputElement;
    const updatedPlaybackRate = Number.parseFloat(inputTarget.value);

    setState(previousState => ({
      ...previousState,
      playbackRate: updatedPlaybackRate,
    }));
  };

  const handlePlaybackRateTouchEnd = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const inputTarget = event.target as HTMLInputElement;
    const updatedPlaybackRate = Number.parseFloat(inputTarget.value);

    sendGA4Tracking({
      action: "click",
      category: "tts",
      customized_parameters: {
        article_id: entityId,
        progress_fraction: played,
        speed: getWPM(updatedPlaybackRate),
      },
      subcategory: "speedchanged",
    });
  };

  const handleSeekChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const inputTarget = event.target as HTMLInputElement;
    const played = Number.parseFloat(inputTarget.value);
    const playedSeconds = played * duration;
    setState(previousState => ({
      ...previousState,
      played,
      playedSeconds,
    }));
  };

  const handleSeekingEnd = (event: React.SyntheticEvent<HTMLInputElement>) => {
    setState(previousState => ({ ...previousState, seeking: false }));
    const inputTarget = event.target as HTMLInputElement;
    const played = Number.parseFloat(inputTarget.value);
    const updatedCurrentTime = played * duration;
    if (playerElement) {
      playerElement.currentTime = updatedCurrentTime;
    }
  };

  const handleSeekingStart = () => {
    setState(previousState => ({ ...previousState, seeking: true }));
  };

  const handleTimeUpdate = () => {
    // Update the time slider if not seeking
    if (!playerElement || seeking) return;

    const updatedPlayedSeconds = playerElement.currentTime;

    const updatedPlayed = updatedPlayedSeconds / duration;

    setState(previousState => ({
      ...previousState,
      played: updatedPlayed,
      playedSeconds: updatedPlayedSeconds,
    }));

    const percentageElapsed = Math.round(updatedPlayed * 100);

    if (percentageElapsed > 0 && percentageElapsed % 10 === 0) {
      const alreadySent = triggeredPercentageList?.current.includes(percentageElapsed);

      if (!alreadySent) {
        triggeredPercentageList?.current.push(percentageElapsed);
        sendGA4Tracking({
          action: "sys",
          category: "tts",
          customized_parameters: {
            action_type: "progress",
            article_id: entityId,
            audio_length: Math.ceil(adjustedDuration),
            progress_fraction: updatedPlayed,
          },
          subcategory: "",
        });
      }
    }

    if (!hasTriggeredThreeSeconds && adjustedDuration && updatedPlayedSeconds >= 3) {
      setState(previousState => ({
        ...previousState,
        hasTriggeredThreeSeconds: true,
      }));

      sendGA4Tracking({
        action: "sys",
        category: "tts",
        customized_parameters: {
          action_type: "3s",
          article_id: entityId,
          audio_length: Math.ceil(adjustedDuration),
          progress_fraction: updatedPlayedSeconds / adjustedDuration,
        },
        subcategory: "",
      });
    }
  };

  const handleBackwardButtonClick = () => {
    if (!playerElement) return;

    setState(previousState => {
      const updatedPlayedSeconds =
        previousState.playedSeconds >= 5
          ? previousState.playedSeconds - 5
          : previousState.playedSeconds;
      playerElement.currentTime = updatedPlayedSeconds;

      const adjustedDuration = previousState.duration / playbackRate;
      return {
        ...previousState,
        played: updatedPlayedSeconds / adjustedDuration,
        playedSeconds: updatedPlayedSeconds,
      };
    });
  };

  const handleForwardButtonClick = () => {
    if (!playerElement) return;

    setState(previousState => {
      const adjustedDuration = previousState.duration / playbackRate;

      const updatedPlayedSeconds =
        previousState.playedSeconds + 5 <= adjustedDuration
          ? previousState.playedSeconds + 5
          : previousState.playedSeconds;
      playerElement.currentTime = updatedPlayedSeconds;

      return {
        ...previousState,
        played: updatedPlayedSeconds / adjustedDuration,
        playedSeconds: updatedPlayedSeconds,
      };
    });
  };

  const getVoiceChangeHandler = (index: number) => () => {
    setState(previousState => ({
      ...previousState,
      hasTriggeredThreeSeconds: false,
    }));

    setState(previousState => ({
      ...previousState,
      played: 0,
      playedSeconds: 0,
      selectedVoiceIndex: index,
      src: voiceNameInfoList[index].url,
    }));

    sendGA4Tracking({
      action: "click",
      category: "tts",
      customized_parameters: {
        article_id: entityId,
        progress_fraction: played,
        voice: voiceNameInfoList[index].voiceName,
      },
      subcategory: "voicechanged",
    });
  };

  const collapseVoiceList = () => {
    setState(previousState => ({
      ...previousState,
      shouldShowVoiceList: false,
    }));
  };

  const toggleShouldShowVoiceList = () => {
    setState(previousState => {
      const updatedShouldShowVoiceList = !previousState.shouldShowVoiceList;

      const updatedShouldShowPlaybackRateSlider = updatedShouldShowVoiceList
        ? false
        : previousState.shouldShowPlaybackRateSlider;

      return {
        ...previousState,
        shouldShowPlaybackRateSlider: updatedShouldShowPlaybackRateSlider,
        shouldShowVoiceList: updatedShouldShowVoiceList,
      };
    });
  };

  const collapsePlaybackRateSlider = () => {
    setState(previousState => ({
      ...previousState,
      shouldShowPlaybackRateSlider: false,
    }));
  };

  const toggleShouldShowPlaybackRateSlider = () => {
    setState(previousState => {
      const updatedShouldShowPlaybackRateSlider = !previousState.shouldShowPlaybackRateSlider;

      const updatedShouldShowVoiceList = updatedShouldShowPlaybackRateSlider
        ? false
        : previousState.shouldShowVoiceList;

      return {
        ...previousState,
        shouldShowPlaybackRateSlider: updatedShouldShowPlaybackRateSlider,
        shouldShowVoiceList: updatedShouldShowVoiceList,
      };
    });
  };

  const isShowingHeader = !shouldShowPlaybackRateSlider && !shouldShowVoiceList;

  const playbackRateMax = 2;
  const playbackRateMin = 0.25;
  const playbackRateStep = 0.25;

  const playbackRatePercentage =
    ((playbackRate - playbackRateMin) / (playbackRateMax - playbackRateMin)) * 100;

  const renderPlayIcon = () => {
    switch (status) {
      case "ended":
        return <StyledRestartIcon />;
      case "hidden":
        return null;
      case "paused":
        return <StyledPlayIcon />;
      case "playing":
        return <StyledPauseIcon />;
    }
  };

  return (
    <Container $isAudioLoaded={!!src && status !== "hidden"}>
      <ReactPlayer
        onDurationChange={handleDurationChange}
        onEnded={handleEnded}
        onPause={handlePause}
        onPlay={handlePlay}
        onTimeUpdate={handleTimeUpdate}
        playbackRate={playbackRate}
        playing={status === "playing"}
        ref={setPlayerReference}
        src={src}
      />
      {collapseStatus === "collapsed" ? (
        <CollapsedPlayerContainer>
          <CollapsedPlayerLeftContainer>
            <CollapsedPlayerPlayIconContainer onClick={handlePlayIconClick}>
              {renderPlayIcon()}
            </CollapsedPlayerPlayIconContainer>
            <RemainingTimeContainer>
              {getDisplayRemainingMinutesAndSeconds(adjustedDuration * (1 - played))}
            </RemainingTimeContainer>
          </CollapsedPlayerLeftContainer>
          <CollapsedPlayerRightContainer>
            <ExpandIconContainer onClick={expandPlayer}>
              <StyledExpandIcon />
            </ExpandIconContainer>
          </CollapsedPlayerRightContainer>
        </CollapsedPlayerContainer>
      ) : (
        <ExpandedPlayerContainer $isShowingHeader={isShowingHeader}>
          <HeaderContainer $isShowingHeader={isShowingHeader}>
            <ThumbnailContainer>
              <ThumbnailImage src={thumbnail} />
            </ThumbnailContainer>
            <Title>{title}</Title>
            <CollapseIconContainer onClick={collapsePlayer}>
              <StyledCollapseIcon />
            </CollapseIconContainer>
          </HeaderContainer>
          <VoiceListContainer $shouldShowVoiceList={shouldShowVoiceList}>
            <div>
              <TitleBar>
                <StyledBlackVoiceIcon />
                <TitleText>Select Voice</TitleText>
                <StyledChevronDownIcon onClick={collapseVoiceList} />
              </TitleBar>
              <VoiceList>
                {voiceNameInfoList.map((info, index) => (
                  <VoiceGrid key={info.voiceName} onClick={getVoiceChangeHandler(index)}>
                    <LeftContainer>
                      <FlagIconContainer>{countryInfoMap[info.country].icon}</FlagIconContainer>
                      <VoiceTextContainer>
                        <VoiceName>{info.voiceName}</VoiceName>
                        <VoiceInfoRow>
                          {info.gender}・{countryInfoMap[info.country].displayName}
                        </VoiceInfoRow>
                      </VoiceTextContainer>
                    </LeftContainer>
                    <RightContainer>
                      {selectedVoiceIndex === index ? <StyledTickIcon /> : <GrayCircle />}
                    </RightContainer>
                  </VoiceGrid>
                ))}
              </VoiceList>
            </div>
          </VoiceListContainer>
          <PlaybackRateSliderContainer $shouldShowPlaybackRateSlider={shouldShowPlaybackRateSlider}>
            <div>
              <TitleBar>
                <TextContainer>
                  <TitleText>Choose your listening speed</TitleText>
                  <SubTitleText>Get through articles 2x faster</SubTitleText>
                </TextContainer>
                <StyledChevronDownIcon onClick={collapsePlaybackRateSlider} />
              </TitleBar>
              <SliderContainer>
                <PopoverAndSlider>
                  <PopoverContainer $percent={playbackRatePercentage}>
                    <PopoverTextContainer>
                      <PopoverPlaybackRateText>{playbackRate}x</PopoverPlaybackRateText>
                      <WPMText>{getWPM(playbackRate).toFixed(0)} WPM</WPMText>
                    </PopoverTextContainer>
                    <StyledPopoverIcon />
                  </PopoverContainer>
                  <Slider
                    max={playbackRateMax}
                    min={playbackRateMin}
                    onChange={handlePlaybackRateChange}
                    onMouseUp={handlePlaybackRateTouchEnd}
                    onTouchEnd={handlePlaybackRateTouchEnd}
                    step={playbackRateStep}
                    type="range"
                    value={playbackRate}
                  />
                </PopoverAndSlider>
                <SliderLabelContainer>
                  <SliderLabel $isSelected={playbackRate < 0.75}>Slow</SliderLabel>
                  <SliderLabel $isSelected={playbackRate >= 0.75 && playbackRate <= 1.5}>
                    Average
                  </SliderLabel>
                  <SliderLabel $isSelected={playbackRate > 1.5}>Fast</SliderLabel>
                </SliderLabelContainer>
              </SliderContainer>
            </div>
          </PlaybackRateSliderContainer>
          <PlayerContainer>
            <SeekBarContainer>
              <Time>{format(played * adjustedDuration)}</Time>
              <SeekBar
                $played={played}
                max={0.999_999}
                min={0}
                onChange={handleSeekChange}
                onMouseDown={handleSeekingStart}
                onMouseUp={handleSeekingEnd}
                onTouchEnd={handleSeekingEnd}
                onTouchStart={handleSeekingStart}
                step="any"
                type="range"
                value={played}
              />
              <Time>{format(adjustedDuration)}</Time>
            </SeekBarContainer>
            <ButtonGroupContainer>
              <StyledGrayVoiceIcon onClick={toggleShouldShowVoiceList} />
              <CenterButtonGroupContainer>
                <StyledBackwardIcon onClick={handleBackwardButtonClick} />
                <PlayIconContainer onClick={handlePlayIconClick}>
                  {renderPlayIcon()}
                </PlayIconContainer>
                <StyledForwardIcon onClick={handleForwardButtonClick} />
              </CenterButtonGroupContainer>
              <PlaybackRateButton onClick={toggleShouldShowPlaybackRateSlider}>
                {playbackRate.toFixed(2)}x
              </PlaybackRateButton>
            </ButtonGroupContainer>
          </PlayerContainer>
        </ExpandedPlayerContainer>
      )}
    </Container>
  );
};
